import { Injectable } from '@angular/core';
import {
  CompanyQPTotalType,
  CompanyQPViewModelCompanyQPTotalTypePagedResultResponse,
  CompanyType,
  ICompanyQPViewModel
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { SearchQuery } from '@view-models/search-query/search-query';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { HttpClientsService } from '../http-clients/http-clients.service';

@Injectable({
  providedIn: 'root'
})
export class StoreClientsService {
  private clientsSub$ = new BehaviorSubject<CompanyQPViewModelCompanyQPTotalTypePagedResultResponse>(null);
  clients$ = this.clientsSub$.asObservable().pipe(map(payload => payload?.records || []));
  totalClients$ = this.clientsSub$
    .asObservable()
    .pipe(map(payload => payload?.totals.find(total => total.type === CompanyQPTotalType.Active)?.value || 0));

  companySelectedSubject$ = new BehaviorSubject<ICompanyQPViewModel>(null);
  companySelected$ = this.companySelectedSubject$.asObservable().pipe(filter(t => t !== null));

  private clientsWihAdminAccessSub$ = new BehaviorSubject<CompanyQPViewModelCompanyQPTotalTypePagedResultResponse>(null);
  clientsWihAdminAccess$ = this.clientsWihAdminAccessSub$
    .asObservable()
    .pipe(map(payload => payload?.records.filter(client => client.isAdmin) || []));

  private clientsToCreateBatchesSub$ = new BehaviorSubject<CompanyQPViewModelCompanyQPTotalTypePagedResultResponse>(null);
  clientsToCreateBatches$ = this.clientsToCreateBatchesSub$.asObservable().pipe(map(payload => payload?.records || []));

  subscriptions = new Subscription();

  get companySelected() {
    return this.companySelectedSubject$.value;
  }
  constructor(private clientsHttp: HttpClientsService) {}

  selectCompany(option: ICompanyQPViewModel) {
    this.companySelectedSubject$.next(option);
  }

  loadClients(uniqueId: string, query: ISearchQuery): void {
    const sub = this.clientsHttp.getClients(uniqueId, query).subscribe(clients => this.clientsSub$.next(clients));
    this.subscriptions.add(sub);
  }
  loadAllClients(uniqueId: string): void {
    const query = new SearchQuery().setPageIndex(undefined).setPageSize(undefined);
    const sub = this.clientsHttp.getClients(uniqueId, query).subscribe(clients => {
      this.clientsSub$.next(clients);
    });
    this.subscriptions.add(sub);
  }
  loadSelectedClient(uniqueId: string, clientUniqueId: string, query: ISearchQuery) {
    return this.clientsHttp
      .getClients(uniqueId, query)
      .toPromise()
      .then(clients => {
        const selectedClient = (clients?.records || []).find(m => m.uniqueId === clientUniqueId);
        this.companySelectedSubject$.next(selectedClient);
        return selectedClient;
      });
  }
  loadClientsWihAdminAccessSub(uniqueId: string): void {
    const query = new SearchQuery().setPageIndex(undefined).setPageSize(undefined);
    const sub = this.clientsHttp.getClients(uniqueId, query).subscribe(clients => this.clientsWihAdminAccessSub$.next(clients));
    this.subscriptions.add(sub);
  }
  loadClientsToCreateBatches(uniqueId: string): void {
    const query = new SearchQuery()
      .appendFilterBy({ property: 'isadmin', equal: 'true' })
      .appendFilterBy({ property: 'companytype', in: [CompanyType.Source, CompanyType.SourceReceiving].toString() });

    const sub = this.clientsHttp.getClients(uniqueId, query).subscribe(clients => this.clientsToCreateBatchesSub$.next(clients));
    this.subscriptions.add(sub);
  }
  clearCompanySelected(): void {
    this.companySelectedSubject$.next(null);
  }
}
